import React, { useMemo } from 'react';
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton
} from '@mui/material';
import { Clear } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

// Clés des pays pour les équipes nationales
const COUNTRY_KEYS = [
  'albania', 'germany', 'angola', 'saudiArabia', 'argentina', 'armenia',
  'australia', 'austria', 'azerbaijan', 'bahamas', 'belize', 'belarus',
  'belgium', 'bosniaHerzegovina', 'brazil', 'bulgaria', 'burkinaFaso', 'burundi',
  'capeVerde', 'cambodia', 'cameroon', 'canada', 'chad', 'chile', 'china', 'cyprus',
  'colombia', 'congo', 'ivoryCoast', 'costaRica', 'croatia', 'cuba', 'denmark',
  'dominica', 'ecuador', 'egypt', 'slovakia', 'slovenia', 'spain', 'unitedStates',
  'estonia', 'finland', 'france', 'gabon', 'gambia', 'georgia', 'ghana', 'greece',
  'guinea', 'guineaBissau', 'guyana', 'haiti', 'hungary', 'iran', 'ireland',
  'iceland', 'britishVirginIslands', 'israel', 'italy', 'jamaica', 'japan', 'kenya',
  'kosovo', 'latvia', 'lebanon', 'lithuania', 'northMacedonia', 'madagascar', 'mali', 'malta',
  'mexico', 'montenegro', 'nicaragua', 'nigeria', 'norway', 'newZealand', 'netherlands',
  'panama', 'poland', 'portugal', 'puertoRico', 'qatar', 'unitedKingdom',
  'centralAfricanRepublic', 'czechRepublic', 'democraticRepublicCongo', 'dominicanRepublic',
  'rwanda', 'romania', 'russia', 'senegal', 'serbia', 'southAfrica', 'sudan',
  'southSudan', 'sweden', 'switzerland', 'tanzania', 'togo', 'trinidadTobago',
  'turkey', 'ukraine', 'uganda', 'uruguay', 'venezuela', 'zimbabwe'
];

// Catégories d'âge pour les équipes nationales
const AGE_CATEGORIES = ['', 'U14', 'U15', 'U16', 'U17', 'U18', 'U19', 'U20'];

function SelectSelectionNational({ value, onChange }) {
  const { t, i18n } = useTranslation();

  // Générer toutes les sélections triées avec traduction
  const sortedSelections = useMemo(() => {
    const allSelections = [];

    COUNTRY_KEYS.forEach(countryKey => {
      AGE_CATEGORIES.forEach(ageCategory => {
        const translatedCountry = t(`createPlayer.countries.${countryKey}`);
        const label = ageCategory ? `${translatedCountry} ${ageCategory}` : translatedCountry;
        const key = ageCategory ? `${countryKey}_${ageCategory}` : countryKey;
        allSelections.push({ key, label, countryKey, ageCategory });
      });
    });

    return allSelections.sort((a, b) => a.label.localeCompare(b.label, i18n.language));
  }, [t, i18n.language]);

  // Fonction pour convertir la clé en label affiché
  const getDisplayLabel = (selectedValue) => {
    if (!selectedValue) return '';

    // Vérifier si c'est une clé connue (format: "angola_U15" ou "angola")
    const found = sortedSelections.find(s => s.key === selectedValue);
    if (found) {
      return found.label;
    }

    // Sinon, c'est une ancienne valeur en base (format: "Angola U15" ou "Angola")
    // On l'affiche telle quelle
    return selectedValue;
  };

  // Gère le clic sur le bouton Clear
  const handleClearClick = (e) => {
    e.stopPropagation();
    onChange({ target: { value: '' } });
  };

  return (
    <FormControl fullWidth>
      <InputLabel>{t('createPlayer.fields.nationalTeam')}</InputLabel>
      <Select
        value={value || ''}
        onChange={onChange}
        label={t('createPlayer.fields.nationalTeam')}
        displayEmpty
        renderValue={(selected) => getDisplayLabel(selected)}
        endAdornment={
          <IconButton
            onClick={handleClearClick}
            sx={{
              visibility: value ? 'visible' : 'hidden',
              position: 'absolute',
              right: 32
            }}
            size="small"
          >
            <Clear fontSize="small" />
          </IconButton>
        }
      >
        <MenuItem value="">
        </MenuItem>
        {/* Si la valeur actuelle est une ancienne valeur (pas une clé), l'ajouter comme option cachée */}
        {value && !sortedSelections.find(s => s.key === value) && (
          <MenuItem value={value} sx={{ display: 'none' }}>
            {value}
          </MenuItem>
        )}
        {sortedSelections.map(({ key, label }) => (
          <MenuItem key={key} value={key}>
            {label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export default SelectSelectionNational;
